import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { HotelQuestion } from "admin/types/hotelQuestion";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { HotelQuestionApplicableConditionTypes } from "../../../constants/hotelQuestionApplicableConditionTypes";

const key = "hotelQuestion";
const adapter = createEntityAdapter<HotelQuestion.Question>();

const initialStateValues: HotelQuestion.HotelQuestionState = {
  hotelQuestion: null,
  errors: null,
  loading: false,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelQuestions = createAsyncThunk<
  {
    questions: HotelQuestion.Question[];
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchQuestions`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/questions.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/questions.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      questions: result.data.hotelQuestions,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelQuestion = createAsyncThunk<
  { question: HotelQuestion.Question },
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelQuestion`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/questions.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/questions.json`;
    const result = await createWrapper().post(url, { hotel_question: params });
    return { question: result.data.hotelQuestion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelQuestion = createAsyncThunk<
  { question: HotelQuestion.Question },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelQuestion`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_questions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_questions/${id}.json`;
    const result = await createWrapper().put(url, { hotel_question: params });
    return { question: result.data.hotelQuestion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelQuestionOrder = createAsyncThunk<
  { question: HotelQuestion.Question },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelQuestionOrder`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_questions/${id}/order.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_questions/${id}/order.json`;
    const result = await createWrapper().put(url, { hotel_question: params });
    return { question: result.data.hotelQuestion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const deleteHotelQuestion = createAsyncThunk<
  { id: number },
  {
    context: PageContext.PageContext;
    id: number;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/deleteHotelQuestion`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotel_questions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotel_questions/${id}.json`;
    const result = await createWrapper().delete(url);
    console.log(result);
    return { id: id };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelQuestionSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelQuestion };
      })
      .addCase(fetchHotelQuestions.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.questions);
      })
      .addCase(postHotelQuestion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.question);
      })
      .addCase(putHotelQuestion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.question);
      })
      .addCase(putHotelQuestionOrder.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.question);
      })
      .addCase(deleteHotelQuestion.fulfilled, (state, action) => {
        const { id } = action.payload;
        adapter.removeOne(state, id);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelQuestionByIdSelector,
  selectIds: hotelQuestionIdsSelector,
  selectEntities: hotelQuestionEntitiesSelector,
  selectAll: hotelQuestionsSelector,
  selectTotal: totalHotelQuestionsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelQuestion);

function compareByOrder(a, b) {
  const aOrder = a.order || 0;
  const bOrder = b.order || 0;
  if (aOrder > bOrder) return 1;
  if (aOrder < bOrder) return -1;
  if (
    ["multiple_line_text", "one_line_text", "multiple_options"].includes(
      a.questionType,
    )
  ) {
    if (
      !["multiple_line_text", "one_line_text", "multiple_options"].includes(
        b.questionType,
      )
    ) {
      return 1;
    }
  } else {
    if (
      ["multiple_line_text", "one_line_text", "multiple_options"].includes(
        b.questionType,
      )
    ) {
      return -1;
    }
  }
  return b.questionType - a.questionType;
}

export const sortedHotelQuestionsSelector = createSelector(
  [hotelQuestionsSelector],
  (all) => {
    return all.sort(compareByOrder);
  },
);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const convertHotelQuestionFormValuesToParams = (data: any) => {
  const params = { ...data };
  delete params.from_checkin_hour;
  delete params.from_checkin_minute;
  delete params.to_checkin_hour;
  delete params.to_checkin_minute;

  if (
    params.applicable_condition === HotelQuestionApplicableConditionTypes.all
  ) {
    delete params.plan_ids;
    params.is_empty_plan_ids = true;
  }

  return params;
};

export const convertHotelQuestionToFormValues = (data: any) => {
  const values = { ...data };
  if (values.planIds) {
    values.plan_ids = values.planIds.map((planId) => String(planId));
    delete values.planIds;
  }
  if (values.range) {
    const fromData = values.range?.from.split(":");
    const toData = values.range?.to.split(":");
    if (fromData && Array.isArray(fromData) && fromData.length === 2) {
      values.from_checkin_hour = fromData[0];
      values.from_checkin_minute = fromData[1];
    }
    if (toData && Array.isArray(toData) && toData.length === 2) {
      values.to_checkin_hour = toData[0];
      values.to_checkin_minute = toData[1];
    }
  }
  return values;
};
